// @ts-nocheck

import debugLib from 'debug';
import * as R from 'ramda';

import deepmerge from 'deepmerge';
import { LOADING_STATUS } from '../../support/constants';
import { toSelectFormat } from '../../support/utils';

const debug = debugLib('LinkCreatorReducer');

export const DEFAULT_LINK_STATE = {
  id: undefined,
  alias: undefined,
  link_id: 'new',
  tags: undefined,
  campaign: undefined,
  channel: undefined,
  feature: undefined,
  loadingStatus: LOADING_STATUS.INIT,
};

// https://dev.branch.io/getting-started/configuring-links/
export const REDIRECT_CONFIGS = [
  '$android_deepview',
  '$android_url',
  '$desktop_deepview',
  '$desktop_url',
  '$ios_deepview',
  '$ios_url',
];
export const DEFAULT_DATA_STATE = {
  $android_deepview: undefined,
  $android_url: undefined,
  $android_tablet_url: undefined,
  $blackberry_url: undefined,
  $desktop_deepview: undefined,
  $desktop_url: undefined,
  $household_measurement: undefined,
  $fire_url: undefined,
  $ios_deepview: undefined,
  $ios_url: undefined,
  $ipad_url: undefined,
  $marketing_title: undefined,
  $og_app_id: undefined,
  $og_description: undefined,
  $og_image_url: undefined,
  $og_title: undefined,
  $windows_phone_url: undefined,
};

export const DEFAULT_CHANNEL_DATA = ['Facebook', 'Twitter', 'Email'];

// defaults for populating link objective dropdown
// maps to 'tags' for analytics purposes
const DEFAULT_TAGS_DATA = [
  'App install',
  'App re-engagement',
  'Drive cross-platform visits',
  'Drive awareness',
  'Drive cross-platform purchases',
];

export const DEFAULT_TEMPLATE_STATE = {
  loadingQlt: false,
  quickLinkTemplate: undefined,
  templateValidationMap: {},
};

export const INIT_STATE = {
  ...DEFAULT_LINK_STATE,
  // don't prepare any init key for data
  data: {},
  date: undefined,
  // if data fetched from scrapper service
  fetchStatus: LOADING_STATUS.INIT,
  updateTime: undefined,
  // URL for scrapper service
  fetchedUrl: undefined,
  error: undefined,
  savedStatus: LOADING_STATUS.INIT,
  // for prefetch list
  channelList: [],
  campaignList: [],
  featureList: [],
  tagsList: toSelectFormat(DEFAULT_TAGS_DATA),
  enabledNetworkList: [],

  ...DEFAULT_TEMPLATE_STATE,
};

/* -------------
 GENERATE PRESET FIELDS
--------------*/
const QuickLinkCreator = 'Quick Link Creator';
const JourneyLinkEditor = 'Journey Link Editor';
const AdsLinkCreator = 'Ads Link Creator';
const editableKeys = {
  // 'reserved_link_data_key': [ list of link features that CAN edit ]
  // For example '$super_key': [ AdsLinkCreator ] <- Means the $super_key CAN be set in the AdsLinkCreator feature

  '~advertising_partner_name': [QuickLinkCreator, JourneyLinkEditor],
  '~branch_ad_format': [],
  '~campaign': [],
  '~campaign_type': [],
  '~channel': [],
  '~creation_source': [],
  '~feature': [],
  '~id': [],
  '~marketing': [],
  '~os_type': [],
  '~secondary_ad_format': [],
  '~stage': [QuickLinkCreator, JourneyLinkEditor],
  '~tags': [],
  '~quick_link_template_id': [],
  '~link_template_id': [],
  '~link_type': [],

  $3p: [QuickLinkCreator],
  $android_deepview: [],
  $android_url: [JourneyLinkEditor],
  $custom_event_attribution_window_days: [],
  $desktop_deepview: [],
  $desktop_url: [JourneyLinkEditor, AdsLinkCreator],
  $household_measurement: [],
  $identity_id: [],
  $ios_deepview: [],
  $ios_url: [JourneyLinkEditor],
  $link_title: [],
  $marketing_title: [],
  $og_description: [],
  $og_image_url: [],
  $og_title: [],
  $one_time_use: [],

  '+created_as_tune_link': [],
  '+match_guaranteed': [],
  '+url': [],
};
const buildPresetList = (feature) => Object.keys(editableKeys).filter((key) => !editableKeys[key].includes(feature));

export const PRESET_FIELDS = buildPresetList(QuickLinkCreator);
export const JOURNEY_PRESET_FIELDS = buildPresetList(JourneyLinkEditor);
export const ADS_PRESET_FIELDS = buildPresetList(AdsLinkCreator);
/* -------------
 END GENERATE PRESET FIELDS
--------------*/

export const LINK_TITLE = {
  marketing: '$marketing_title',
  ad: '$link_title',
};

/**
 * REDUCER HELPER FUNCTIONS
 */

/**
 * Determine if link is a marketing link
 */
export function getLinkTitleKey(data = {}) {
  return R.has('$marketing_title', data) ? '$marketing_title' : '$link_title';
}

/**
 * Merged data with scraper service output
 * @param  {Object} state current state
 * @param  {Object} payload data get from scraper service
 * @return {Object}       merged state
 */
export function mergedWithScraperData(state, payload, isMarketing) {
  if (!payload) {
    debug('No data returns from scraper service');
    return state;
  }
  debug('Merge current state with scraper data', state, payload);
  // { $og_app_id, $og_title, $android_deeplink_path,
  //   $og_description, $canonical_url, "$og_type", post_id }
  const linkData = payload.link_data;
  const linkTitleKey = isMarketing ? '$marketing_title' : '$link_title';

  // If user provide name already, don't backfill from service
  const name = state.data[linkTitleKey] || linkData.$og_title || payload.title;

  const data = R.mergeAll([
    state.data,
    linkData,
    {
      [linkTitleKey]: name,
      $og_title: linkData.$og_title || payload.title,
      $og_description: (linkData.$og_description || payload.description || '').substr(0, 254),
    },
  ]);
  return {
    ...state,
    data,
    $og_title: linkData.$og_title || payload.title,
    $og_description: (linkData.$og_description || payload.description || '').substr(0, 254),
    fetchStatus: LOADING_STATUS.LOADED,
    fetchedError: undefined,
  };
}

/**
 * Clear scraper service data
 * @param  {Object} state current state
 * @param  {Object} app   app data
 * @return {Object}       clear state
 */
export const clearScraperData = (state, app) => {
  debug('clear current state from scraper data');

  const { og_title, og_description, og_image_url } = app || {};

  const data = {
    $marketing_title: state.data.$marketing_title,
    $og_title: og_title,
    $og_description: og_description,
    $og_image_url: og_image_url,
  };
  return {
    data,
    fetchStatus: LOADING_STATUS.INIT,
    fetchedUrl: undefined,
  };
};

/**
 * merge current state with app config
 * @param  {Object} state     current state
 * @param  {Object} app			  app config
 * @return {Object}           merged state
 */
export const mergedWithAppConfig = (state, app = {}) => {
  debug('Merge current state with app config', state, app);
  const { default_short_url_domain, name, short_url_domain } = app;

  return deepmerge(state, {
    appName: name,
    domain: short_url_domain || default_short_url_domain,
    /**
     * ad links don't use custom domain (short_url_domain). Use app.link instead
     */
    defaultDomain: default_short_url_domain,
    /*
     * used for showing tooltips explaining why ad links don't use custom domains
     */
    hasCustomDomain: !!short_url_domain,
  });
};
